import { useEffect, useRef, useState, type FormEvent, type RefObject } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { recordMfaChallengeFailure, setMfaClientSession, mfaClient } from "../api/mfaAuthClient";
import { Button } from "../components/Button";
import { StatusBanner } from "../components/StatusBanner";
import { useOnlineStatus } from "../hooks/useOnlineStatus";

const CODE_LENGTH = 6;
const REQUEST_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
type ViewState = "loading" | "ready" | "verifying" | "error" | "success";

function safeReturnPath(value: string | null, requestId: string | null): string {
  if (value === "/mfa/recuperacao/aprovar") {
    const normalizedRequestId = requestId?.trim() ?? "";
    return REQUEST_ID.test(normalizedRequestId)
      ? `/mfa/recuperacao/aprovar?request_id=${encodeURIComponent(normalizedRequestId)}`
      : "/mfa/recuperacao/aprovar";
  }
  return value && value.startsWith("/") && !value.startsWith("//") ? value : "/dashboard";
}

function auditFailure(factorId: string, reason: "invalid_code" | "challenge_expired" | "challenge_unavailable") {
  void recordMfaChallengeFailure(factorId, reason).catch(() => {
    // A falha da auditoria não recebe detalhes do GoTrue nem do código no console. A sessão continua AAL1.
  });
}

async function findVerifiedTotpFactor(accessToken: string, refreshToken: string) {
  await setMfaClientSession(accessToken, refreshToken);
  const { data, error } = await mfaClient.auth.mfa.listFactors();
  if (error) throw error;
  return data?.totp.find((factor) => factor.status === "verified") ?? null;
}

async function loadChallengeFactor(options: {
  accessToken: string;
  refreshToken: string;
  isCancelled: () => boolean;
  expireSession: () => void;
  setFactorId: (factorId: string) => void;
  setError: (error: string | null) => void;
  setView: (view: ViewState) => void;
}) {
  const { accessToken, refreshToken, isCancelled, expireSession, setFactorId, setError, setView } = options;
  try {
    const factor = await findVerifiedTotpFactor(accessToken, refreshToken);
    if (!factor) {
      if (!isCancelled()) {
        setError("Não há um autenticador verificado disponível para esta confirmação.");
        setView("error");
      }
      return;
    }
    if (!isCancelled()) {
      setFactorId(factor.id);
      setView("ready");
    }
  } catch (caught) {
    if ((caught as { status?: number }).status === 401) {
      expireSession();
      return;
    }
    if (!isCancelled()) {
      setError("Não foi possível preparar a confirmação agora. Tente novamente.");
      setView("error");
    }
  }
}

type ChallengeAttempt =
  | { status: "success"; accessToken: string; refreshToken: string; expiresIn: number }
  | { status: "unauthorized" }
  | { status: "challenge-unavailable" }
  | { status: "invalid-code" };

async function runChallenge(factorId: string, code: string): Promise<ChallengeAttempt> {
  const { data: challenge, error: challengeError } = await mfaClient.auth.mfa.challenge({ factorId });
  if (challengeError) {
    return challengeError.status === 401 ? { status: "unauthorized" } : { status: "challenge-unavailable" };
  }
  const { data: verified, error: verifyError } = await mfaClient.auth.mfa.verify({
    factorId,
    challengeId: challenge.id,
    code,
  });
  if (verifyError) {
    return verifyError.status === 401 ? { status: "unauthorized" } : { status: "invalid-code" };
  }
  return { status: "success", accessToken: verified.access_token, refreshToken: verified.refresh_token, expiresIn: verified.expires_in };
}

interface SubmitChallengeOptions {
  event: FormEvent<HTMLFormElement>;
  state: { status: string };
  factorId: string | null;
  code: string;
  online: boolean;
  expireSession: () => void;
  applyMfaVerifiedSession: (session: { accessToken: string; refreshToken: string; expiresIn: number }) => void;
  setView: (view: ViewState) => void;
  setError: (error: string | null) => void;
  setCode: (code: string) => void;
  navigate: ReturnType<typeof useNavigate>;
  returnPath: string | null;
  requestId: string | null;
}

async function submitChallenge(options: SubmitChallengeOptions) {
  const { event, state, factorId, code, online, expireSession, applyMfaVerifiedSession, setView, setError, setCode, navigate, returnPath, requestId } = options;
  event.preventDefault();
  if (state.status !== "authenticated" || !factorId || code.length !== CODE_LENGTH || !online) return;
  setView("verifying");
  setError(null);
  try {
    const result = await runChallenge(factorId, code);
    if (result.status === "unauthorized") { expireSession(); return; }
    if (result.status === "challenge-unavailable") {
      auditFailure(factorId, "challenge_unavailable");
      setError("Não foi possível confirmar agora. A ação não foi realizada. Tente novamente em instantes.");
      setView("ready");
      return;
    }
    if (result.status === "invalid-code") {
      auditFailure(factorId, "invalid_code");
      setCode("");
      setError("Código não confere. Confira o app do autenticador e tente de novo.");
      setView("ready");
      return;
    }
    applyMfaVerifiedSession({ accessToken: result.accessToken, refreshToken: result.refreshToken, expiresIn: result.expiresIn });
    setView("success");
    window.setTimeout(() => navigate(safeReturnPath(returnPath, requestId), { replace: true }), 500);
  } catch {
    auditFailure(factorId, "challenge_unavailable");
    setError("Não foi possível confirmar agora. A ação não foi realizada. Tente novamente em instantes.");
    setView("ready");
  }
}

function MfaChallengeView(props: {
  online: boolean;
  error: string | null;
  view: ViewState;
  code: string;
  inputRef: RefObject<HTMLInputElement>;
  setCode: (code: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onRetry: () => void;
}) {
  const { online, error, view, code, inputRef, setCode, onSubmit, onRetry } = props;
  return (
    <main className="centered-page mfa-challenge-page">
      <section className="card mfa-challenge-card" aria-labelledby="mfa-challenge-title">
        <p className="mfa-challenge__eyebrow">Confirmação de segurança</p>
        <h1 id="mfa-challenge-title">Confirme sua identidade</h1>
        <p className="card__subtitle">Esta ação precisa de uma confirmação pelo seu aplicativo autenticador.</p>
        {!online && <StatusBanner tone="warning">Sem conexão. Verifique a internet e tente novamente.</StatusBanner>}
        {error && <StatusBanner tone="error">{error}</StatusBanner>}
        {view === "loading" && <p role="status">Preparando a confirmação…</p>}
        {view === "success" && <p role="status">Identidade confirmada. Continuando…</p>}
        {view === "ready" || view === "verifying" ? (
          <form onSubmit={onSubmit} noValidate>
            <div className="field">
              <label className="field__label" htmlFor="mfa-challenge-code">Código do aplicativo autenticador</label>
              <input
                ref={inputRef}
                id="mfa-challenge-code"
                className="field__input"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={CODE_LENGTH}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH))}
                disabled={view === "verifying"}
                aria-describedby="mfa-challenge-help"
              />
              <span className="field__hint" id="mfa-challenge-help">Informe os 6 dígitos exibidos no autenticador.</span>
            </div>
            <Button type="submit" loading={view === "verifying"} disabled={!online || code.length !== CODE_LENGTH}>
              Confirmar e continuar
            </Button>
          </form>
        ) : null}
        {view === "error" && <Button type="button" onClick={onRetry} disabled={!online}>Tentar novamente</Button>}
      </section>
    </main>
  );
}

export function MfaChallengePage() {
  const { state, expireSession, applyMfaVerifiedSession } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const online = useOnlineStatus();
  const [view, setView] = useState<ViewState>("loading");
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (state.status !== "authenticated") return;
    let cancelled = false;
    void loadChallengeFactor({ accessToken: state.accessToken, refreshToken: state.refreshToken, isCancelled: () => cancelled, expireSession, setFactorId, setError, setView });
    return () => { cancelled = true; };
  }, [expireSession, state.status === "authenticated" ? state.accessToken : null]);

  useEffect(() => {
    if (view === "ready") inputRef.current?.focus();
  }, [view]);

  if (state.status !== "authenticated") return null;
  return <MfaChallengeView
    online={online}
    error={error}
    view={view}
    code={code}
    inputRef={inputRef}
    setCode={setCode}
    onSubmit={(event) => void submitChallenge({ event, state, factorId, code, online, expireSession, applyMfaVerifiedSession, setView, setError, setCode, navigate, returnPath: params.get("retorno"), requestId: params.get("request_id") })}
    onRetry={() => window.location.reload()}
  />;
}
